import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator, CheckCircle, X, AlertTriangle, Package,
  Calendar, MapPin, User, FileText, Download,
  Upload, Plus, Search, Filter, RefreshCw, Eye,
  Edit, Trash2, MoreHorizontal, ChevronRight,
  Clock, DollarSign, Scale, Box, Camera, QrCode,
  Barcode, Scan, Printer, Bell, UserCheck, Store,
  Home, Database, PieChart, LineChart, BarChart2,
  Target, Building2, Users, Timer, CheckSquare,
  Square, Percent, TrendingUp, TrendingDown
} from 'lucide-react';

const InventoryCounting = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [showCamera, setShowCamera] = useState(false);
  const [scanningMode, setScanningMode] = useState('barcode');
  const [countingMode, setCountingMode] = useState('manual'); // 'manual' or 'camera'

  // Mock data for inventory counting
  const countingData = [
    {
      id: 1,
      countNumber: 'CNT-2024-001',
      location: 'Warehouse A',
      status: 'scheduled',
      scheduledDate: '2024-01-20',
      startDate: null,
      completedDate: null,
      type: 'Full Count',
      assignedTo: 'John Smith',
      supervisor: 'Mike Wilson',
      items: [
        { sku: 'INV-001', name: 'Premium Coffee Beans', expected: 150, counted: 0, variance: 0, unit: 'kg', barcode: '1234567890123', status: 'pending' },
        { sku: 'INV-005', name: 'Premium Tea Selection', expected: 200, counted: 0, variance: 0, unit: 'boxes', barcode: '1234567890127', status: 'pending' }
      ],
      totalItems: 2,
      completedItems: 0,
      accuracy: 0,
      notes: 'Monthly full inventory count',
      images: ['warehouse-a-overview.jpg'],
      documents: ['count-schedule-001.pdf'],
      priority: 'high',
      estimatedDuration: '4 hours',
      teamSize: 3,
      equipment: ['Barcode Scanners', 'Tablets', 'Counting Sheets']
    },
    {
      id: 2,
      countNumber: 'CNT-2024-002',
      location: 'Cold Storage',
      status: 'in-progress',
      scheduledDate: '2024-01-18',
      startDate: '2024-01-18',
      completedDate: null,
      type: 'Cycle Count',
      assignedTo: 'Sarah Johnson',
      supervisor: 'Mike Wilson',
      items: [
        { sku: 'INV-002', name: 'Organic Milk', expected: 15, counted: 12, variance: -3, unit: 'liters', barcode: '1234567890124', status: 'completed' }
      ],
      totalItems: 1,
      completedItems: 1,
      accuracy: 80,
      notes: 'Weekly cycle count for high-value items',
      images: ['cold-storage-count.jpg', 'milk-count.jpg'],
      documents: ['cycle-count-002.pdf'],
      priority: 'medium',
      estimatedDuration: '2 hours',
      teamSize: 2,
      equipment: ['Temperature Sensors', 'Barcode Scanners']
    },
    {
      id: 3,
      countNumber: 'CNT-2024-003',
      location: 'Bakery Section',
      status: 'completed',
      scheduledDate: '2024-01-15',
      startDate: '2024-01-15',
      completedDate: '2024-01-15',
      type: 'Spot Check',
      assignedTo: 'Lisa Brown',
      supervisor: 'Mike Wilson',
      items: [
        { sku: 'INV-003', name: 'Artisan Bread', expected: 0, counted: 0, variance: 0, unit: 'pieces', barcode: '1234567890125', status: 'completed' }
      ],
      totalItems: 1,
      completedItems: 1,
      accuracy: 100,
      notes: 'Daily spot check completed',
      images: ['bakery-count.jpg'],
      documents: ['spot-check-003.pdf', 'completion-report-003.pdf'],
      priority: 'low',
      estimatedDuration: '1 hour',
      teamSize: 1,
      equipment: ['Tablet']
    },
    {
      id: 4,
      countNumber: 'CNT-2024-004',
      location: 'Produce Section',
      status: 'discrepancy',
      scheduledDate: '2024-01-17',
      startDate: '2024-01-17',
      completedDate: '2024-01-17',
      type: 'Full Count',
      assignedTo: 'David Lee',
      supervisor: 'Mike Wilson',
      items: [
        { sku: 'INV-004', name: 'Fresh Vegetables Mix', expected: 75, counted: 68, variance: -7, unit: 'kg', barcode: '1234567890126', status: 'completed' }
      ],
      totalItems: 1,
      completedItems: 1,
      accuracy: 90.7,
      notes: 'Significant variance detected - investigation required',
      images: ['produce-count.jpg', 'variance-report-004.jpg'],
      documents: ['full-count-004.pdf', 'variance-report-004.pdf', 'investigation-004.pdf'],
      priority: 'high',
      estimatedDuration: '3 hours',
      teamSize: 2,
      equipment: ['Scales', 'Barcode Scanners']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500 text-white';
      case 'in-progress': return 'bg-yellow-500 text-white';
      case 'completed': return 'bg-green-500 text-white';
      case 'discrepancy': return 'bg-red-500 text-white';
      case 'cancelled': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <Calendar className="w-4 h-4" />;
      case 'in-progress': return <Calculator className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'discrepancy': return <AlertTriangle className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Full Count': return 'bg-purple-100 text-purple-800';
      case 'Cycle Count': return 'bg-blue-100 text-blue-800';
      case 'Spot Check': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = countingData.filter(item => {
    const matchesSearch = item.countNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.assignedTo.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = selectedLocation === 'all' || item.location === selectedLocation;
    const matchesType = selectedType === 'all' || item.type === selectedType;
    const matchesStatus = activeTab === 'all' || item.status === activeTab;
    return matchesSearch && matchesLocation && matchesType && matchesStatus;
  });

  const tabs = [
    { id: 'all', label: 'All', count: countingData.length },
    { id: 'scheduled', label: 'Scheduled', count: countingData.filter(item => item.status === 'scheduled').length },
    { id: 'in-progress', label: 'In Progress', count: countingData.filter(item => item.status === 'in-progress').length },
    { id: 'completed', label: 'Completed', count: countingData.filter(item => item.status === 'completed').length },
    { id: 'discrepancy', label: 'Discrepancy', count: countingData.filter(item => item.status === 'discrepancy').length },
    { id: 'cancelled', label: 'Cancelled', count: countingData.filter(item => item.status === 'cancelled').length }
  ];

  const locations = [...new Set(countingData.map(item => item.location))];
  const countTypes = [...new Set(countingData.map(item => item.type))];

  const CountingCard = ({ item }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{item.countNumber}</h3>
            <p className="text-sm text-gray-600">{item.location}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(item.status)}`}>
              {getStatusIcon(item.status)}
              <span>{item.status.replace('-', ' ').toUpperCase()}</span>
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
              {item.type}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
              {item.priority.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Progress Information */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Progress</span>
            <span className="text-sm text-blue-600">{item.completedItems}/{item.totalItems} items</span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(item.completedItems / item.totalItems) * 100}%` }}
            />
          </div>
          {item.accuracy > 0 && (
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-blue-600">Accuracy</span>
              <span className="text-xs font-medium text-blue-900">{item.accuracy}%</span>
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Scheduled Date</p>
            <p className="text-sm font-medium text-gray-900">{new Date(item.scheduledDate).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Assigned To</p>
            <p className="text-sm font-medium text-gray-900">{item.assignedTo}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Team Size</p>
            <p className="text-sm font-medium text-gray-900">{item.teamSize} people</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Est. Duration</p>
            <p className="text-sm font-medium text-gray-900">{item.estimatedDuration}</p>
          </div>
        </div>

        {/* Items List */}
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Items to Count</h4>
          <div className="space-y-2">
            {item.items.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">{product.name}</p>
                  <p className="text-xs text-gray-600">SKU: {product.sku} • Barcode: {product.barcode}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">
                    {product.counted}/{product.expected} {product.unit}
                  </p>
                  {product.variance !== 0 && (
                    <p className={`text-xs ${product.variance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.variance > 0 ? '+' : ''}{product.variance} variance
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Equipment */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg">
          <h4 className="text-sm font-medium text-green-900 mb-2">Equipment Required</h4>
          <div className="flex flex-wrap gap-2">
            {item.equipment.map((equipment, index) => (
              <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                {equipment}
              </span>
            ))}
          </div>
        </div>

        {/* Notes */}
        {item.notes && (
          <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
            <p className="text-sm text-yellow-800">{item.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-2">
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Camera className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <QrCode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Barcode className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <Calculator className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {item.status === 'scheduled' && (
              <button className="px-3 py-1 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors">
                Start Count
              </button>
            )}
            {item.status === 'in-progress' && (
              <button className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                Complete
              </button>
            )}
            {item.status === 'discrepancy' && (
              <button className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Investigate
              </button>
            )}
            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Header with Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Inventory Counting</h2>
            <p className="text-gray-600">Manage physical inventory counts and accuracy</p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCamera(!showCamera)}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Camera Count
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Count
            </motion.button>
          </div>
        </div>

        {/* Camera Scanner */}
        {showCamera && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Counting Scanner</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCountingMode('manual')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    countingMode === 'manual' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Manual
                </button>
                <button
                  onClick={() => setCountingMode('camera')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    countingMode === 'camera' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Camera
                </button>
                <button
                  onClick={() => setScanningMode('barcode')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'barcode' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Barcode className="w-4 h-4 inline mr-1" />
                  Barcode
                </button>
                <button
                  onClick={() => setScanningMode('qr')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    scanningMode === 'qr' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <QrCode className="w-4 h-4 inline mr-1" />
                  QR Code
                </button>
              </div>
            </div>
            <div className="bg-black rounded-lg h-64 flex items-center justify-center">
              <div className="text-center text-white">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm opacity-75">
                  {countingMode === 'camera' ? 'Camera Counting Mode' : 'Manual Counting Mode'}
                </p>
                <p className="text-xs opacity-50 mt-1">
                  {scanningMode === 'barcode' ? 'Scan barcodes to count items' : 'Scan QR codes to count items'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id 
                    ? 'bg-gray-900 text-white' 
                    : 'bg-gray-200 text-gray-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search counts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
            />
          </div>
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="all">All Locations</option>
            {locations.map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
          >
            <option value="all">All Types</option>
            {countTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <button className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2">
            <Filter className="w-4 h-4" />
            Advanced
          </button>
        </div>
      </div>

      {/* Counting Items Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnimatePresence mode="wait">
          {filteredData.map((item) => (
            <CountingCard key={item.id} item={item} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="text-center py-12">
          <Calculator className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No counting items found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
          <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
            Create New Count
          </button>
        </div>
      )}
    </div>
  );
};

export default InventoryCounting; 